import React from 'react';

import { useTranslation } from 'react-i18next';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import type { TemplateSection } from 'inspection-types';
import { S } from 'components/typography';

import { formatSectionTitle } from '../../helpers/inspectionTemplateCustomizeHelpers';

import { Item } from './SectionCardItem';
import styles from './InspectionTemplateCustomizeSectionCard.scss';

type Props = {
  section: TemplateSection;
  sectionIndex: number;
  expanded: boolean;
  onToggle: () => void;
  removeSectionDisabledTitle: string;
  removeItemDisabledTitle: string;
};

export function SectionCard({
  section,
  sectionIndex,
  expanded,
  onToggle,
  removeSectionDisabledTitle,
  removeItemDisabledTitle,
}: Props) {
  const { t } = useTranslation();

  const itemCount = section.items.length;
  const items = [...section.items].sort((a, b) => a.order - b.order);

  return (
    <div id={`section-${section.id}`} className={styles.sectionCard}>
      <div className={styles.sectionHeaderRow}>
        <button
          type="button"
          className={styles.sectionToggle}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={styles.rowLead} aria-hidden>
            <Icon
              name={expanded ? 'chevron-down' : 'chevron-right'}
              set={ICON_SET_FONTAWESOME}
              type="fas"
              className={styles.sectionChevron}
            />
          </span>
          <span className={styles.sectionBody}>
            <span className={styles.sectionTitle}>
              {formatSectionTitle(sectionIndex, section.name)}
            </span>
            <S variant="secondary" className={styles.sectionItemsIncluded}>
              {t('web.admin.inspections.templateCustomize.itemsIncluded', {
                count: itemCount,
                included: itemCount,
                total: itemCount,
              })}
            </S>
          </span>
        </button>
        <div className={styles.rowActionCell}>
          <button
            type="button"
            className={styles.rowActionButton}
            disabled
            title={removeSectionDisabledTitle}
            aria-label={t(
              'web.admin.inspections.templateCustomize.removeSectionAria'
            )}
          >
            <Icon name="trash-alt" set={ICON_SET_FONTAWESOME} type="far" />
          </button>
        </div>
      </div>
      {expanded &&
        items.map((item, itemIndex) => (
          <Item
            key={item.id}
            item={item}
            sectionIndex={sectionIndex}
            itemIndex={itemIndex}
            removeItemDisabledTitle={removeItemDisabledTitle}
          />
        ))}
    </div>
  );
}
